import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { format, parseISO } from "date-fns";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { api } from "../lib/api";
import { useLanguage, dfLocale } from "../i18n";

const fmtInline = (s) =>
  s.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : part
  );

const renderContent = (text) =>
  text.split(/\n\n+/).map((block, i) => {
    const b = block.trim();
    if (b.startsWith("## ")) {
      return (
        <h2 key={i} className="mt-10 text-xl font-bold tracking-tight text-antracite sm:text-2xl">
          {fmtInline(b.slice(3))}
        </h2>
      );
    }
    const lines = b.split("\n");
    if (lines.every((l) => l.startsWith("- "))) {
      return (
        <ul key={i} className="mt-6 space-y-2.5">
          {lines.map((l, j) => (
            <li key={j} className="flex items-start gap-2.5 text-base leading-relaxed text-antracite/75">
              <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-elettrico" />
              <span>{fmtInline(l.slice(2))}</span>
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className="mt-6 text-base leading-relaxed text-antracite/75 sm:text-lg">
        {fmtInline(b)}
      </p>
    );
  });

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setPost(null);
    setMissing(false);
    api.get(`/posts/${slug}?lang=${lang}`).then((r) => setPost(r.data)).catch(() => setMissing(true));
  }, [slug, lang]);

  if (missing) {
    return (
      <section className="pt-40 pb-28 text-center" data-testid="blogpost-missing">
        <h1 className="text-2xl font-bold text-antracite">{t("Nessun articolo disponibile al momento.")}</h1>
        <Link to="/blog" className="btn-secondary mt-8 inline-flex" data-testid="blogpost-back-missing">
          <ArrowLeft className="h-4 w-4" /> {t("Torna al blog")}
        </Link>
      </section>
    );
  }
  if (!post) return null;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    inLanguage: post.lang,
    datePublished: post.created_at,
    dateModified: post.updated_at,
    author: { "@type": "Organization", name: "Alsera Studio" },
  };

  return (
    <>
      <Seo title={`${post.title} — Alsera Studio`} description={post.excerpt || post.title} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <article className="pt-32 pb-24 sm:pt-40" data-testid="blogpost">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-bold text-elettrico transition-all duration-300 hover:gap-3" data-testid="blogpost-back">
              <ArrowLeft className="h-4 w-4" /> {t("Torna al blog")}
            </Link>
            <p className="mt-8 text-xs font-semibold uppercase tracking-[0.14em] text-antracite/50">
              {format(parseISO(post.created_at), "d MMMM yyyy", { locale: dfLocale[lang] })} · {post.minutes} {t("min di lettura")}
            </p>
            <h1 className="mt-4 text-3xl font-extrabold leading-[1.15] tracking-tight text-antracite sm:text-4xl" data-testid="blogpost-title">
              {post.title}
            </h1>
            {post.excerpt && (
              <p className="mt-5 text-base leading-relaxed text-antracite/65 sm:text-lg">{post.excerpt}</p>
            )}
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-4" data-testid="blogpost-content">{renderContent(post.content)}</div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-14 rounded-3xl bg-petrolio p-8 text-avorio sm:p-10" data-testid="blogpost-cta">
              <h2 className="text-xl font-bold tracking-tight sm:text-2xl">{t("Vuoi migliorare la tua presenza online?")}</h2>
              <p className="mt-3 text-sm leading-relaxed text-avorio/75 sm:text-base">
                {t("Raccontaci la tua attività: ti aiutiamo a fare il prossimo passo, senza impegno.")}
              </p>
              <Link to="/contatti" className="mt-6 inline-flex items-center gap-2 rounded-full bg-avorio px-7 py-3.5 text-sm font-semibold text-petrolio shadow-lg transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-xl" data-testid="blogpost-cta-btn">
                {t("Contattaci")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </article>
    </>
  );
}
